import {
  Badge,
  Box,
  Flex,
  GridItem,
  Icon,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';

import type { InvoicingBilling } from 'api/quote-marketplace';
import type { FieldConfig, FieldGroupConfig } from './fieldGroups';

const FieldValue = ({
  field,
  billing,
}: {
  field: FieldConfig;
  billing: InvoicingBilling;
}) => {
  if ('component' in field) return <field.component billing={billing} />;
  if ('pending' in field) {
    return (
      <Badge colorScheme="gray" variant="subtle">
        Not available yet
      </Badge>
    );
  }
  const display = field.value(billing);
  const note = field.note?.(billing);
  return (
    <Tooltip label={note} hasArrow isDisabled={!note}>
      <Text
        as="span"
        tabIndex={note ? 0 : undefined}
        borderBottom={note ? '1px dotted' : undefined}
        aria-label={display ? undefined : 'Empty'}
      >
        {display || '—'}
      </Text>
    </Tooltip>
  );
};

const BillingFieldGroup = ({
  group,
  billing,
}: {
  group: FieldGroupConfig;
  billing: InvoicingBilling;
}) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const fieldBg = useColorModeValue('#f4f5f7', 'navy.900');
  const border = useColorModeValue('gray.100', 'gray.600');

  return (
    <Box>
      <Flex align="center" gap="2" color={muted} mb="3">
        <Icon as={group.icon} />
        <Text as="h5" fontSize="xs" fontWeight="700" textTransform="uppercase">
          {group.title}
        </Text>
      </Flex>
      <SimpleGrid as="dl" columns={{ base: 1, sm: 2, lg: 4 }} spacing="3">
        {group.fields.map((field, i) => {
          const label =
            typeof field.label === 'function'
              ? field.label(billing)
              : field.label;
          return (
            <GridItem key={i} minW="0">
              <Text
                as="dt"
                fontSize="11px"
                fontWeight="600"
                textTransform="uppercase"
                color={muted}
                mb="1"
              >
                {label}
              </Text>
              <Box
                as="dd"
                m="0"
                bg={fieldBg}
                borderWidth="1px"
                borderColor={border}
                borderRadius="md"
                px="3"
                py="2"
                minH="38px"
                fontSize="sm"
                overflowWrap="anywhere"
              >
                <FieldValue field={field} billing={billing} />
              </Box>
            </GridItem>
          );
        })}
      </SimpleGrid>
      {group.footer && <group.footer billing={billing} />}
    </Box>
  );
};

export default BillingFieldGroup;
